import { createContext, useContext, useEffect, useId, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type {
  AiAdminScope,
  AiCatalogModel,
  AiCatalogModelWrite,
  AiCredentialMode,
  AiModelPrices,
  AiModelRoute,
  AiOrgRouting,
  AiProviderConnection,
  AiProviderCredentials,
  AiProviderKind,
  AiProviderUpdate,
  AiRouteClass,
  AiRouteTarget,
  AiTestResult,
} from "@backstory/protocol";
import { ErrorState, Skeleton } from "../../components/States";
import { ConfirmDialog, DataTable, Dialog, Field, PageHeader, RowMenu, SearchSelect, SectionTabs, Switch, useToast, type Column } from "../../components/ui";
import { DirtyActions, settingsEqual, UnsavedGuard } from "../../components/UnsavedChanges";
import { platformAiAdmin, type AiAdminClient } from "./api";
import "./ai-admin.css";

/**
 * Which set of connections and routes this screen is editing. Everything below
 * is written once and rendered for both: an organization configuring its own AI
 * gets the platform operator's screen, not a reduced version of it. The default
 * is the platform so the staff and self-hosted mounts need no wrapper.
 */
const AiAdminScopeContext = createContext<{ client: AiAdminClient; scope: AiAdminScope }>({
  client: platformAiAdmin,
  scope: "platform",
});

export function AiAdminScopeProvider({ client, scope, children }: { client: AiAdminClient; scope: AiAdminScope; children: ReactNode }) {
  const value = useMemo(() => ({ client, scope }), [client, scope]);
  return <AiAdminScopeContext.Provider value={value}>{children}</AiAdminScopeContext.Provider>;
}

function useAiAdmin() {
  return useContext(AiAdminScopeContext);
}

const PROVIDER_NAMES: Record<AiProviderKind, string> = {
  openai: "OpenAI",
  anthropic: "Anthropic",
  google_ai: "Google AI",
  vertex_ai: "Vertex AI",
  bedrock: "Amazon Bedrock",
  azure: "Azure AI",
};
/**
 * A cheap, widely available model per vendor, offered as the default for the one
 * call that proves a credential works. Every field is editable, because an
 * account with its own allowlist or an Azure deployment name will not match a
 * default, and a failed probe against a model nobody subscribed to says nothing
 * about the key.
 */
const VERIFY_MODELS: Record<AiProviderKind, string> = {
  openai: "gpt-4o-mini",
  anthropic: "claude-3-5-haiku-latest",
  google_ai: "gemini-2.0-flash",
  vertex_ai: "gemini-2.0-flash",
  bedrock: "anthropic.claude-3-5-haiku-20241022-v1:0",
  azure: "",
};

function defaultMode(provider: AiProviderKind): AiCredentialMode {
  return provider === "vertex_ai" ? "workload_identity" : provider === "bedrock" ? "default_credentials" : "api_key";
}

const BASE_ROUTES: Array<{ value: AiRouteClass; label: string }> = [
  { value: "reasoning", label: "Reasoning" },
  { value: "standard", label: "Standard" },
  { value: "cheap", label: "Fast" },
  { value: "embed", label: "Embeddings" },
];

type SecretDraft = {
  mode?: AiCredentialMode;
  apiKey?: string;
  project?: string;
  location?: string;
  serviceAccountJson?: string;
  region?: string;
  roleArn?: string;
  accessKeyId?: string;
  secretAccessKey?: string;
  sessionToken?: string;
  apiBase?: string;
  apiVersion?: string;
};

function initialMode(provider: AiProviderConnection): AiCredentialMode {
  return provider.credentialMode ?? defaultMode(provider.provider);
}

function credentials(provider: AiProviderKind, draft: SecretDraft): AiProviderCredentials | undefined {
  if (provider === "vertex_ai") {
    if (!draft.project?.trim() || !draft.location?.trim()) return undefined;
    if (draft.mode === "service_account") {
      if (!draft.serviceAccountJson?.trim()) return undefined;
      return { mode: "service_account", project: draft.project.trim(), location: draft.location.trim(), serviceAccountJson: draft.serviceAccountJson };
    }
    return { mode: "workload_identity", project: draft.project.trim(), location: draft.location.trim() };
  }
  if (provider === "bedrock") {
    if (!draft.region?.trim()) return undefined;
    if (draft.mode === "access_key") {
      if (!draft.accessKeyId?.trim() || !draft.secretAccessKey?.trim()) return undefined;
      return { mode: "access_key", region: draft.region.trim(), accessKeyId: draft.accessKeyId.trim(), secretAccessKey: draft.secretAccessKey, sessionToken: draft.sessionToken?.trim() || undefined, roleArn: draft.roleArn?.trim() || undefined };
    }
    if (draft.mode === "iam_role") {
      if (!draft.roleArn?.trim()) return undefined;
      return { mode: "iam_role", region: draft.region.trim(), roleArn: draft.roleArn.trim() };
    }
    return { mode: "default_credentials", region: draft.region.trim(), roleArn: draft.roleArn?.trim() || undefined };
  }
  if (provider === "azure") {
    if (!draft.apiKey?.trim() || !draft.apiBase?.trim()) return undefined;
    return { mode: "api_key", apiKey: draft.apiKey, apiBase: draft.apiBase.trim(), apiVersion: draft.apiVersion?.trim() || undefined };
  }
  return draft.apiKey?.trim() ? { mode: "api_key", apiKey: draft.apiKey } : undefined;
}

/**
 * `bedrockOnly` and `phiOnly` are separate answers to separate questions. A PHI
 * route is restricted to Bedrock only where the deployment's own agreement with
 * AWS is what makes it safe; an organization serving its own PHI work picks its
 * own vendors, so it may use any connection and is warned instead of blocked.
 */
export function validateRoute(route: AiModelRoute, providers: AiProviderConnection[]): string | null {
  if (!route.targets.length) return "Add a primary model.";
  const seen = new Set<string>();
  for (const row of route.targets) {
    if (!row.providerConnectionId || !row.model.trim()) return "Every row needs a provider connection and model identifier.";
    const provider = providers.find((item) => item.id === row.providerConnectionId);
    if (!provider?.enabled || !provider.configured) return "Every route must use a configured, enabled provider.";
    if (route.bedrockOnly && provider.provider !== "bedrock") return "This PHI route may only use Amazon Bedrock.";
    const key = `${row.providerConnectionId}\0${row.model.trim()}`;
    if (seen.has(key)) return "A provider and model pair cannot appear twice in one route.";
    seen.add(key);
  }
  return null;
}

const providerDraft = (provider: AiProviderConnection): SecretDraft => ({
  mode: initialMode(provider),
  project: provider.project,
  location: provider.location,
  region: provider.region,
  roleArn: provider.roleArn,
  apiBase: provider.apiBase,
  apiVersion: provider.apiVersion,
});

function ProviderCard({ provider }: { provider: AiProviderConnection }) {
  const { client: api } = useAiAdmin();
  const qc = useQueryClient();
  const toast = useToast();
  const deleteFormId = useId();
  const [editing, setEditing] = useState(!provider.configured);
  const [enabled, setEnabled] = useState(provider.enabled);
  const [savedEnabled, setSavedEnabled] = useState(provider.enabled);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [displayName, setDisplayName] = useState(provider.displayName);
  const [savedDisplayName, setSavedDisplayName] = useState(provider.displayName);
  const [draft, setDraft] = useState<SecretDraft>(() => providerDraft(provider));
  const [savedDraft, setSavedDraft] = useState<SecretDraft>(() => providerDraft(provider));
  const [model, setModel] = useState(VERIFY_MODELS[provider.provider]);
  const verify = useVerification(provider.provider, draft, model);
  useEffect(() => {
    setEnabled(provider.enabled);
    setSavedEnabled(provider.enabled);
    setDisplayName(provider.displayName);
    setSavedDisplayName(provider.displayName);
    const next = providerDraft(provider);
    setDraft(next);
    setSavedDraft(next);
  }, [provider.enabled, provider.displayName]);
  const dirty = enabled !== savedEnabled || displayName !== savedDisplayName || (editing && !settingsEqual(draft, savedDraft));
  const discard = () => {
    setEnabled(savedEnabled);
    setDisplayName(savedDisplayName);
    setDraft(savedDraft);
    setEditing(!provider.configured);
  };
  const refresh = () => void qc.invalidateQueries({ queryKey: api.keys.config });
  const save = useMutation({
    mutationFn: (update: AiProviderUpdate) => api.updateProvider(provider.id, update),
    onSuccess: () => {
      const clean = { mode: draft.mode, project: draft.project, location: draft.location, region: draft.region, roleArn: draft.roleArn, apiBase: draft.apiBase, apiVersion: draft.apiVersion };
      setDraft(clean);
      setSavedDraft(clean);
      setSavedEnabled(enabled);
      setSavedDisplayName(displayName.trim());
      setDisplayName(displayName.trim());
      setEditing(false);
      refresh();
      toast.push("success", `${PROVIDER_NAMES[provider.provider]} saved`);
    },
    onError: (error) => toast.push("error", (error as Error).message),
  });
  const clear = useMutation({
    mutationFn: () => api.clearProvider(provider.id),
    onSuccess: () => { setClearOpen(false); setEditing(true); refresh(); toast.push("success", "Credentials cleared"); },
    onError: (error) => toast.push("error", (error as Error).message),
  });
  const test = useMutation({
    mutationFn: () => api.testProvider(provider.id),
    onSuccess: (result) => toast.push(result.ok ? "success" : "error", result.message),
    onError: (error) => toast.push("error", (error as Error).message),
  });
  const remove = useMutation({
    mutationFn: () => api.deleteProvider(provider.id),
    onSuccess: () => {
      setDeleteOpen(false);
      setDeleteConfirmation("");
      refresh();
      toast.push("success", `${provider.displayName} deleted`);
    },
    onError: (error) => toast.push("error", (error as Error).message),
  });
  // What Save would send, and whether there is anything complete enough to send.
  // The button is disabled on the same answer, so a half-filled form says so
  // before it is submitted rather than in a toast afterwards. New credentials
  // have to be verified first, the same as when the connection was added; a save
  // that only renames or disables it does not.
  const nextCredentials = editing ? credentials(provider.provider, draft) : undefined;
  const complete = !!displayName.trim() && (!editing || (!!nextCredentials && verify.passed));
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!complete) return;
    save.mutate({
      enabled,
      displayName: displayName.trim(),
      ...(nextCredentials ? { credentials: nextCredentials, models: verify.models } : {}),
    });
  };
  const set = (patch: Partial<SecretDraft>) => setDraft((value) => ({ ...value, ...patch }));
  const closeDelete = () => {
    if (remove.isPending) return;
    setDeleteOpen(false);
    setDeleteConfirmation("");
  };

  return (
    <>
      <form className="card ai-provider-card" onSubmit={submit} autoComplete="off" data-testid={`provider-${provider.provider}`}>
        <header>
          <div>
            <h2>{provider.displayName}</h2>
            <div className="muted small">{PROVIDER_NAMES[provider.provider]}</div>
          </div>
          <span className={`badge ${provider.configured ? "ok" : "neutral"}`}>{provider.configured ? "Configured" : "Not configured"}</span>
          <Switch checked={enabled} onChange={setEnabled} label="Enabled" />
        </header>
        <div className="ai-provider-body">
          <Field label="Connection name" hint="Shown when selecting providers in model routing.">
            {(p) => <input {...p} className="input" value={displayName} onChange={(e) => setDisplayName(e.target.value)} maxLength={100} required />}
          </Field>
          {provider.configured && !editing ? (
            <div className="ai-secret-summary">
              <span className="muted small">
                Credentials are stored securely and cannot be viewed.
                {provider.apiBase ? ` Endpoint ${provider.apiBase}.` : ""}
              </span>
              <div className="actions-row">
                <button type="button" className="btn" onClick={() => setEditing(true)}>Replace</button>
                <button type="button" className="btn danger" onClick={() => setClearOpen(true)}>Clear</button>
              </div>
            </div>
          ) : (
            <>
              <CredentialFields provider={provider.provider} draft={draft} set={set} />
              <VerifyCredentials
                provider={provider.provider}
                draft={draft}
                model={model}
                setModel={setModel}
                state={verify}
                ready={!!nextCredentials}
              />
            </>
          )}
        </div>
        <footer className="actions-row">
          <UnsavedGuard dirty={dirty} />
          <DirtyActions dirty={dirty} onDiscard={discard}>
            <button type="submit" className="btn primary" disabled={!complete || save.isPending || !dirty}>Save connection</button>
          </DirtyActions>
          {/* Tests what is stored, which is only a question worth asking when the
              form is not holding a replacement for it. */}
          {!editing && <button type="button" className="btn" onClick={() => test.mutate()} disabled={!provider.configured || test.isPending}>Test connection</button>}
          {editing && provider.configured && <button type="button" className="btn ghost" onClick={() => { setDraft(savedDraft); setEditing(false); }}>Cancel</button>}
          <span className="spacer" />
          <RowMenu label={`Actions for ${provider.displayName}`} testId={`provider-actions-${provider.id}`}>
            <button type="button" role="menuitem" className="danger" onClick={() => setDeleteOpen(true)}>Delete connection</button>
          </RowMenu>
        </footer>
      </form>
      <ConfirmDialog
        open={clearOpen}
        danger
        title="Clear credentials"
        confirmLabel={clear.isPending ? "Clearing…" : "Clear credentials"}
        busy={clear.isPending}
        onConfirm={() => clear.mutate()}
        onClose={() => setClearOpen(false)}
      >
        <p>The stored credentials for {provider.displayName} are deleted. Requests routed to it fail until new ones are saved.</p>
      </ConfirmDialog>
      <Dialog
        open={deleteOpen}
        onClose={closeDelete}
        title="Delete provider connection"
        width={440}
        danger
        footer={
          <>
            <button type="button" className="btn ghost" onClick={closeDelete} disabled={remove.isPending}>Cancel</button>
            <button
              type="submit"
              form={deleteFormId}
              className="btn danger"
              disabled={deleteConfirmation !== "CONFIRM" || remove.isPending}
            >
              {remove.isPending ? "Deleting…" : "Delete connection"}
            </button>
          </>
        }
      >
        <form
          id={deleteFormId}
          className="ai-delete-confirm"
          autoComplete="off"
          onSubmit={(event) => {
            event.preventDefault();
            if (deleteConfirmation === "CONFIRM") remove.mutate();
          }}
        >
          <p>
            This permanently deletes <strong>{provider.displayName}</strong>. Type <code>CONFIRM</code> to continue.
          </p>
          <Field label="Confirmation">
            {(p) => (
              <input
                {...p}
                className="input ai-delete-confirm-input"
                value={deleteConfirmation}
                onChange={(event) => setDeleteConfirmation(event.target.value.toUpperCase())}
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                maxLength={7}
                aria-label="Type CONFIRM to delete"
              />
            )}
          </Field>
        </form>
      </Dialog>
    </>
  );
}

/**
 * A connection, its credentials and the proof that they work, in one dialog. The
 * connection used to be created from a name alone and configured afterwards,
 * which left an unconfigured record in the list for anyone who stopped halfway,
 * and a key nobody had tried until the first real request needed it.
 */
function AddProviderConnection({ onClose }: { onClose: () => void }) {
  const { client: api } = useAiAdmin();
  const qc = useQueryClient();
  const toast = useToast();
  const formId = useId();
  const [provider, setProvider] = useState<AiProviderKind>("openai");
  const [displayName, setDisplayName] = useState("");
  const [draft, setDraft] = useState<SecretDraft>({ mode: defaultMode("openai") });
  const [model, setModel] = useState(VERIFY_MODELS.openai);
  const verify = useVerification(provider, draft, model);
  const ready = credentials(provider, draft);
  const create = useMutation({
    mutationFn: () => api.createProvider({ provider, displayName: displayName.trim(), credentials: ready!, models: verify.models }),
    onSuccess: (connection) => {
      void qc.invalidateQueries({ queryKey: api.keys.config });
      onClose();
      toast.push("success", `${connection.displayName} added and enabled.`);
    },
    onError: (error) => toast.push("error", (error as Error).message),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title="Add provider connection"
      width={520}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
          {/* Submits the form in the body, which is where the fields and their
              validation live. */}
          <button type="submit" form={formId} className="btn primary" disabled={!displayName.trim() || !ready || !verify.passed || create.isPending}>Add connection</button>
        </>
      }
    >
      <form
        id={formId}
        className="ai-add-provider"
        autoComplete="off"
        onSubmit={(event) => {
          event.preventDefault();
          if (displayName.trim() && ready && verify.passed) create.mutate();
        }}
      >
        <Field label="Provider">
          {(p) => (
            <SearchSelect
              id={p.id}
              aria-describedby={p["aria-describedby"]}
              aria-invalid={p["aria-invalid"]}
              value={provider}
              options={Object.entries(PROVIDER_NAMES).map(([value, label]) => ({ value, label }))}
              onChange={(value) => {
                const next = value as AiProviderKind;
                setProvider(next);
                setDraft({ mode: defaultMode(next) });
                setModel(VERIFY_MODELS[next]);
              }}
              searchPlaceholder="Search providers…"
            />
          )}
        </Field>
        {/* The same wording the connection's own card uses for this field. */}
        <Field label="Connection name" hint="Shown when selecting providers in model routing.">
          {(p) => <input {...p} className="input" value={displayName} onChange={(e) => setDisplayName(e.target.value)} maxLength={100} required />}
        </Field>
        <CredentialFields provider={provider} draft={draft} set={(patch) => setDraft((value) => ({ ...value, ...patch }))} />
        <VerifyCredentials provider={provider} draft={draft} model={model} setModel={setModel} state={verify} ready={!!ready} />
      </form>
    </Dialog>
  );
}

/**
 * Proves a connection before saving it. OpenAI lists the models available to the
 * draft key, which validates it without a guessed completion model; providers
 * without discovery make one call to the explicit model or deployment below.
 */
function VerifyCredentials({
  provider,
  draft,
  model,
  setModel,
  state,
  ready,
}: {
  provider: AiProviderKind;
  draft: SecretDraft;
  model: string;
  setModel: (value: string) => void;
  state: ReturnType<typeof useVerification>;
  ready: boolean;
}) {
  const discoversModels = provider === "openai";
  const complete = ready && (discoversModels || !!model.trim());
  return (
    <div className="ai-verify">
      {!discoversModels && (
        <Field
          label={provider === "azure" ? "Deployment to test with" : "Model to test with"}
          hint="One call is made to it now. It does not limit which models the routes may use."
        >
          {(p) => <input {...p} className="input" value={model} onChange={(e) => setModel(e.target.value)} placeholder={VERIFY_MODELS[provider]} />}
        </Field>
      )}
      <div className="ai-verify-row">
        <button type="button" className="btn" onClick={() => state.run({ provider, model: model.trim(), draft })} disabled={!complete || state.pending}>
          {state.pending ? "Testing…" : state.passed ? "Test again" : "Test connection"}
        </button>
        {state.result && (
          <span className={`small ${state.passed ? "ai-verify-ok" : "ai-validation"}`} role="status">
            {state.result.message}
          </span>
        )}
        {!state.result && <span className="small muted">Required before this connection can be saved.</span>}
      </div>
    </div>
  );
}

/**
 * Holds one verification result, and drops it the moment the credentials it was
 * about change: a pass carried over to an edited key would be the gate letting
 * through exactly what it exists to catch.
 */
function useVerification(provider: AiProviderKind, draft: SecretDraft, model: string) {
  const { client: api } = useAiAdmin();
  const toast = useToast();
  const [result, setResult] = useState<AiTestResult | null>(null);
  const [verified, setVerified] = useState<string | null>(null);
  const fingerprint = JSON.stringify({ provider, draft, model: provider === "openai" ? "" : model.trim() });
  useEffect(() => {
    setResult(null);
    setVerified(null);
  }, [fingerprint]);
  const run = useMutation({
    mutationFn: ({ provider, model, draft }: { provider: AiProviderKind; model: string; draft: SecretDraft }) => {
      const body = credentials(provider, draft);
      return api.verifyProvider({ provider, model, credentials: body! });
    },
    onSuccess: (outcome, variables) => {
      setResult(outcome);
      setVerified(
        outcome.ok
          ? JSON.stringify({
              provider: variables.provider,
              draft: variables.draft,
              model: variables.provider === "openai" ? "" : variables.model.trim(),
            })
          : null,
      );
    },
    onError: (error) => {
      setResult(null);
      setVerified(null);
      toast.push("error", (error as Error).message);
    },
  });
  return {
    result,
    models: verified === fingerprint ? result?.models : undefined,
    pending: run.isPending,
    passed: verified === fingerprint,
    run: (variables: { provider: AiProviderKind; model: string; draft: SecretDraft }) => run.mutate(variables),
  };
}

function CredentialFields({ provider, draft, set }: { provider: AiProviderKind; draft: SecretDraft; set: (patch: Partial<SecretDraft>) => void }) {
  if (provider === "vertex_ai") {
    const mode = draft.mode === "service_account" ? "service_account" : "workload_identity";
    return (
      <div className="ai-credential-fields">
        <Field label="Authentication method">{(p) => <SearchSelect id={p.id} aria-describedby={p["aria-describedby"]} aria-invalid={p["aria-invalid"]} value={mode} onChange={(value) => set({ mode: value as AiCredentialMode })} options={[{ value: "workload_identity", label: "Workload identity" }, { value: "service_account", label: "Service-account JSON" }]} searchPlaceholder="Search authentication methods…" />}</Field>
        <div className="field-row">
          <Field label="Google Cloud project">{(p) => <input {...p} className="input" value={draft.project ?? ""} onChange={(e) => set({ project: e.target.value })} required />}</Field>
          <Field label="Location">{(p) => <input {...p} className="input" value={draft.location ?? ""} onChange={(e) => set({ location: e.target.value })} placeholder="us-central1" required />}</Field>
        </div>
        {mode === "service_account" && <Field label="Service-account JSON" hint="Paste JSON or choose a JSON file. It is write-only.">{(p) => <><textarea {...p} className="input ai-secret-textarea" value={draft.serviceAccountJson ?? ""} onChange={(e) => set({ serviceAccountJson: e.target.value })} required /><input aria-label="Upload service-account JSON" type="file" accept="application/json,.json" autoComplete="off" onChange={(e) => { const file = e.target.files?.[0]; if (file) void file.text().then((text) => set({ serviceAccountJson: text })); }} /></>}</Field>}
      </div>
    );
  }
  if (provider === "bedrock") {
    const mode = draft.mode === "iam_role" || draft.mode === "access_key" ? draft.mode : "default_credentials";
    return (
      <div className="ai-credential-fields">
        <Field label="Authentication method">{(p) => <SearchSelect id={p.id} aria-describedby={p["aria-describedby"]} aria-invalid={p["aria-invalid"]} value={mode} onChange={(value) => set({ mode: value as AiCredentialMode })} options={[{ value: "default_credentials", label: "Default workload identity" }, { value: "iam_role", label: "IAM role" }, { value: "access_key", label: "Access keys" }]} searchPlaceholder="Search authentication methods…" />}</Field>
        <Field label="AWS region">{(p) => <input {...p} className="input" value={draft.region ?? ""} onChange={(e) => set({ region: e.target.value })} placeholder="us-east-1" required />}</Field>
        {(mode === "iam_role" || mode === "default_credentials" || mode === "access_key") && <Field label={`Role ARN${mode === "iam_role" ? "" : " (optional)"}`}>{(p) => <input {...p} className="input" value={draft.roleArn ?? ""} onChange={(e) => set({ roleArn: e.target.value })} required={mode === "iam_role"} />}</Field>}
        {mode === "access_key" && <div className="field-row">
          <Field label="Access key ID">{(p) => <input {...p} className="input" type="password" value={draft.accessKeyId ?? ""} onChange={(e) => set({ accessKeyId: e.target.value })} required />}</Field>
          <Field label="Secret access key">{(p) => <input {...p} className="input" type="password" value={draft.secretAccessKey ?? ""} onChange={(e) => set({ secretAccessKey: e.target.value })} required />}</Field>
          <Field label="Session token" hint="Optional.">{(p) => <input {...p} className="input" type="password" value={draft.sessionToken ?? ""} onChange={(e) => set({ sessionToken: e.target.value })} />}</Field>
        </div>}
      </div>
    );
  }
  if (provider === "azure") {
    return (
      <div className="ai-credential-fields">
        <Field label="Resource endpoint" hint="The Azure OpenAI or Azure AI Foundry resource URL.">
          {(p) => <input {...p} className="input" value={draft.apiBase ?? ""} onChange={(e) => set({ apiBase: e.target.value })} placeholder="https://your-resource.openai.azure.com" required />}
        </Field>
        <Field label="API version" hint="Optional. Azure’s documented version, for example 2024-10-21.">
          {(p) => <input {...p} className="input" value={draft.apiVersion ?? ""} onChange={(e) => set({ apiVersion: e.target.value })} placeholder="2024-10-21" />}
        </Field>
        <Field label="API key" hint="Write-only. Saved keys are never returned to this browser.">
          {(p) => <input {...p} className="input" type="password" value={draft.apiKey ?? ""} onChange={(e) => set({ apiKey: e.target.value })} required />}
        </Field>
      </div>
    );
  }
  return <Field label="API key" hint="Write-only. Saved keys are never returned to this browser.">{(p) => <input {...p} className="input" type="password" value={draft.apiKey ?? ""} onChange={(e) => set({ apiKey: e.target.value })} required />}</Field>;
}

/** Labels the classes an organization still has to fill in but has never saved. */
function routeLabel(routeClass: string): string {
  const base = BASE_ROUTES.find((item) => item.value === routeClass.replace(/^phi-/, ""));
  const name = base?.label ?? routeClass;
  return routeClass.startsWith("phi-") ? `${name} (PHI)` : name;
}

/**
 * The models a connection may route to, asked of the gateway that holds its key:
 * the same credential that serves AI traffic is what answers the provider's model
 * list, so no key has to be re-entered and none has to be stored twice.
 *
 * Providers that cannot be listed — an Azure deployment name is nobody's to
 * enumerate — fall back to typing the identifier, with a placeholder naming what
 * that vendor calls it rather than offering Azure's word to every provider.
 */
/**
 * Keeps the model a route already serves selectable even when it is no longer on
 * the offered list, because it was removed from the priced list or withdrawn by
 * the vendor. The row is describing what this route points at today, and dropping
 * it silently would change that on the next save.
 */
function withCurrent(models: string[], current: string): string[] {
  return !current || models.includes(current) ? models : [current, ...models];
}

function RouteModelField({
  provider,
  target,
  label,
  onChange,
}: {
  provider?: AiProviderConnection;
  target: AiRouteTarget;
  label: string;
  onChange: (model: string) => void;
}) {
  const { client, scope } = useAiAdmin();
  // What a platform route may serve is the priced list, not everything a key can
  // reach: the platform bills for these calls, so a model nobody priced would be
  // served at a loss. Offering the whole provider list here is what produced a
  // refusal at save time, after the choice looked accepted.
  const priced = scope === "platform" && !!client.catalog;
  const catalog = useQuery({
    queryKey: client.keys.catalog,
    queryFn: () => client.catalog!(),
    enabled: priced,
    staleTime: 5 * 60 * 1000,
  });
  const listable = !priced && !!provider?.configured && provider.provider !== "azure";
  const discovery = useQuery({
    queryKey: client.keys.models(provider?.id ?? ""),
    queryFn: () => client.providerModels(provider!.id),
    enabled: listable,
    staleTime: 5 * 60 * 1000,
  });
  if (priced) {
    if (catalog.isPending) {
      return <input className="input" aria-label={label} autoComplete="off" value={target.model} readOnly placeholder="Loading models…" />;
    }
    const vendor = provider?.provider;
    // Only what is turned on: a priced model that is switched off is being held
    // back from new routes on purpose.
    const known = (catalog.data?.models ?? []).filter((m) => m.enabled !== false && (!vendor || m.provider === vendor)).map((m) => m.model);
    return (
      <SearchSelect
        aria-label={label}
        value={target.model}
        onChange={onChange}
        options={withCurrent(known, target.model).map((model) => ({ value: model, label: model }))}
        placeholder="Select model"
        searchPlaceholder="Search models…"
        empty={vendor ? `No ${PROVIDER_NAMES[vendor]} models are turned on yet. Turn one on under Models.` : "Choose a provider connection first."}
      />
    );
  }
  const known = discovery.data?.models ?? provider?.models ?? [];
  if (listable && discovery.isPending) {
    return <input className="input" aria-label={label} autoComplete="off" value={target.model} readOnly placeholder="Loading models…" />;
  }
  if (known.length) {
    return (
      <SearchSelect
        aria-label={label}
        value={target.model}
        onChange={onChange}
        options={withCurrent(known, target.model).map((model) => ({ value: model, label: model }))}
        placeholder="Select model"
        searchPlaceholder="Search models…"
      />
    );
  }
  return (
    <input
      className="input"
      aria-label={label}
      autoComplete="off"
      value={target.model}
      onChange={(event) => onChange(event.target.value)}
      placeholder={provider?.provider === "azure" ? "Deployment name" : VERIFY_MODELS[provider?.provider ?? "openai"] || "Model identifier"}
    />
  );
}

function RoutingEditor({ initial, providers, required }: { initial: AiModelRoute[]; providers: AiProviderConnection[]; required?: readonly AiRouteClass[] }) {
  const { client: api, scope } = useAiAdmin();
  const toast = useToast();
  const qc = useQueryClient();
  // A class an organization has not saved yet still needs a tab, or there would
  // be no way to fill in the routes activation requires. When `required` is
  // given it is the authoritative list, so unrelated base classes are not
  // offered to a PHI organization and vice versa.
  const tabs = useMemo(() => {
    const classes = required?.length ? [...required] : BASE_ROUTES.map((item) => item.value);
    for (const route of initial) if (!classes.includes(route.class)) classes.push(route.class);
    return classes.map((value) => ({ value, label: initial.find((route) => route.class === value)?.label ?? routeLabel(value) }));
  }, [initial, required]);
  const [selected, setSelected] = useState<AiRouteClass>(tabs[0]!.value);
  const [routes, setRoutes] = useState<AiModelRoute[]>(initial);
  const [savedRoutes, setSavedRoutes] = useState<AiModelRoute[]>(initial);
  useEffect(() => {
    setRoutes(initial);
    setSavedRoutes(initial);
  }, [initial]);
  const phi = selected.startsWith("phi-");
  const stored = routes.find((item) => item.class === selected);
  const route: AiModelRoute = stored ?? { class: selected, targets: [], ...(phi ? { phiOnly: true, bedrockOnly: scope === "platform" } : {}) };
  const initialStored = savedRoutes.find((item) => item.class === selected);
  const initialRoute: AiModelRoute = initialStored ?? { class: selected, targets: [], ...(phi ? { phiOnly: true, bedrockOnly: scope === "platform" } : {}) };
  const routeDirty = !settingsEqual(route, initialRoute);
  const anyDirty = !settingsEqual(routes, savedRoutes);
  // Each class saves on its own, so the notice belongs to the open tab. The
  // other tabs carry a dot instead, because a page-wide banner could not say
  // which of them was edited.
  const tabDirty = (cls: string) => !settingsEqual(routes.find((item) => item.class === cls), savedRoutes.find((item) => item.class === cls));
  const discardRoute = () => setRoutes((all) => {
    const saved = savedRoutes.find((item) => item.class === selected);
    if (!saved) return all.filter((item) => item.class !== selected);
    return all.some((item) => item.class === selected) ? all.map((item) => (item.class === selected ? saved : item)) : [...all, saved];
  });
  const allowed = providers.filter((provider) => provider.enabled && provider.configured && (!route.bedrockOnly || provider.provider === "bedrock"));
  const error = validateRoute(route, providers);
  const save = useMutation({
    mutationFn: api.updateRoute,
    onSuccess: () => {
      setSavedRoutes((all) => all.some((item) => item.class === selected) ? all.map((item) => item.class === selected ? route : item) : [...all, route]);
      void qc.invalidateQueries({ queryKey: api.keys.config });
      toast.push("success", `${route.label ?? tabs.find((tab) => tab.value === selected)?.label} route saved`);
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const test = useMutation({
    mutationFn: () => api.testRoute(selected),
    onSuccess: (result) => toast.push(result.ok ? "success" : "error", result.message),
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const update = (targets: AiRouteTarget[]) => setRoutes((all) => {
    const next = { ...route, targets };
    return all.some((item) => item.class === selected) ? all.map((item) => item.class === selected ? next : item) : [...all, next];
  });
  const move = (index: number, delta: number) => {
    const copy = [...route.targets];
    const destination = index + delta;
    if (destination < 0 || destination >= copy.length) return;
    [copy[index], copy[destination]] = [copy[destination]!, copy[index]!];
    update(copy);
  };

  return (
    <section className="card ai-routing">
      <UnsavedGuard dirty={anyDirty} />
      <div className="ai-route-tabs" role="tablist" aria-label="Model routing classes">
        {/* Just the class names. Whether one has models yet is said by the panel
            below it, not decorated onto the tab. */}
        {tabs.map((tab) => (
          <button key={tab.value} type="button" role="tab" aria-selected={selected === tab.value} onClick={() => setSelected(tab.value)} className="ai-route-tab">
            {tab.label}
            {tabDirty(tab.value) && <span className="dirty-dot" title="Unsaved changes" />}
          </button>
        ))}
      </div>
      {route.bedrockOnly && <div className="ai-phi-notice"><strong>PHI route.</strong> Only configured Amazon Bedrock connections may be selected.</div>}
      {phi && !route.bedrockOnly && (
        <div className="ai-phi-notice" role="note">
          <strong>PHI route on your own providers.</strong> Whatever you select here will receive protected health information. Backstory makes no compliance
          claim about it: you are responsible for having a business associate agreement with the provider you choose.
        </div>
      )}
      <div className="ai-route-list" data-testid={`route-${selected}`}>
        {/* Column headings once, rather than a label on every row: a fallback
            chain is read down a column, and repeating them turned five rows into
            fifteen lines of text. */}
        {route.targets.length > 0 && (
          <div className="ai-route-row ai-route-head" aria-hidden="true">
            <span>Order</span>
            <span>Provider connection</span>
            <span>Model identifier</span>
            <span />
          </div>
        )}
        {route.targets.map((target, index) => {
          const rowLabel = index === 0 ? "Primary" : `Backup ${index}`;
          const selectedProvider = providers.find((provider) => provider.id === target.providerConnectionId);
          return (
            <div className="ai-route-row" key={`${index}-${target.providerConnectionId}`}>
              <span className={`badge ${index === 0 ? "ai" : "neutral"}`}>{rowLabel}</span>
              <SearchSelect
                aria-label={`${rowLabel} provider connection`}
                value={target.providerConnectionId}
                onChange={(value) => update(route.targets.map((row, i) => i === index ? { ...row, providerConnectionId: value, model: "" } : row))}
                options={allowed.map((provider) => ({ value: provider.id, label: provider.displayName || PROVIDER_NAMES[provider.provider] }))}
                placeholder="Select provider"
                searchPlaceholder="Search provider connections…"
              />
              <RouteModelField
                provider={selectedProvider}
                target={target}
                label={`${rowLabel} model identifier`}
                onChange={(model) => update(route.targets.map((row, i) => i === index ? { ...row, model } : row))}
              />
              <div className="actions-row">
                <button type="button" className="btn sm" aria-label={`Move row ${index + 1} up`} onClick={() => move(index, -1)} disabled={index === 0}>↑</button>
                <button type="button" className="btn sm" aria-label={`Move row ${index + 1} down`} onClick={() => move(index, 1)} disabled={index === route.targets.length - 1}>↓</button>
                <button type="button" className="btn sm danger" aria-label={`Remove row ${index + 1}`} onClick={() => update(route.targets.filter((_, i) => i !== index))}>Remove</button>
              </div>
            </div>
          );
        })}
        {/* A row whose only option is "Select provider" cannot be saved, so an
            editor with nothing to route says what is missing instead of handing
            over controls that dead-end. */}
        {route.targets.length === 0 && (
          <p className="muted ai-route-empty">
            {allowed.length === 0
              ? route.bedrockOnly
                ? "This route may only use Amazon Bedrock, and no Bedrock connection is configured and enabled yet. Add one under Providers."
                : "No configured connection to route to yet. Add one under Providers, then come back to choose its models."
              : "No models yet."}
          </p>
        )}
      </div>
      <div className="ai-route-footer">
        <button type="button" className="btn" onClick={() => update([...route.targets, { providerConnectionId: allowed[0]?.id ?? "", model: "" }])} disabled={allowed.length === 0}>{route.targets.length ? "Add backup" : "Add primary"}</button>
        <span className="spacer" />
        {/* An untouched class is not a mistake, and the empty state above already
            says what to add, so the red text waits until a row is actually wrong. */}
        {error && route.targets.length > 0 && <span className="small ai-validation" role="alert">{error}</span>}
        <button type="button" className="btn" onClick={() => test.mutate()} disabled={!!error || test.isPending}>Test route</button>
        <DirtyActions dirty={routeDirty} onDiscard={discardRoute}>
          <button type="button" className="btn primary" onClick={() => save.mutate(route)} disabled={!!error || save.isPending || !routeDirty}>Save route</button>
        </DirtyActions>
      </div>
    </section>
  );
}

/**
 * Connecting a provider and deciding which model serves which class are separate
 * jobs done at different times, so they are separate pages: together they made
 * one screen where neither had room, and saving a credential appeared to be part
 * of editing a fallback chain.
 */
export function AiProvidersPage() {
  const { client: api } = useAiAdmin();
  const query = useQuery({ queryKey: api.keys.config, queryFn: api.config, retry: false });
  const [adding, setAdding] = useState(false);
  if (query.isPending) return <Skeleton lines={10} />;
  if (query.isError) return <ErrorState error={query.error} retry={() => void query.refetch()} />;
  const count = query.data.providers.length;
  return (
    <section aria-label="Provider connections">
      <div className="ai-provider-toolbar">
        <p className="muted ai-admin-lede">
          Credentials are write-only: a saved secret can be replaced or cleared, never read back. A connection has to be configured and enabled before a route
          may use it.
        </p>
        <div className="ai-provider-toolbar-actions">
          {/* "0 connections" next to an empty list is a label for nothing: the
              empty state below says it in a sentence. */}
          {count > 0 && <span className="muted small">{count} connection{count === 1 ? "" : "s"}</span>}
          <button type="button" className="btn primary" onClick={() => setAdding(true)}>Add provider connection</button>
        </div>
      </div>
      <StagedNotice routing={query.data.organizationRouting} />
      {adding && <AddProviderConnection onClose={() => setAdding(false)} />}
      <div className="ai-provider-list">
        {query.data.providers.map((provider) => <ProviderCard key={provider.id} provider={provider} />)}
        {count === 0 && (
          <div className="card empty ai-provider-empty">
            <strong>No provider connections yet</strong>
            <span>Add an account with its credentials, then route each class of work to one of its models.</span>
          </div>
        )}
      </div>
    </section>
  );
}

/**
 * Shown only in an organization scope, and only while its routing is not the
 * live source: without it, an administrator editing a complete set of routes has
 * no way to tell that nothing they save is serving traffic yet.
 */
function StagedNotice({ routing }: { routing?: AiOrgRouting }) {
  if (!routing || routing.active) return null;
  return (
    <div className="card ai-staged-notice" role="note">
      <strong>Staged, not live.</strong>{" "}
      {routing.ready
        ? "Every required route is configured. Turn on your own providers under AI general settings to start using them."
        : `Finish these routes before you can switch over: ${routing.missingClasses.map(routeLabel).join(", ")}.`}
    </div>
  );
}

export function AiRoutingPage() {
  const { client: api } = useAiAdmin();
  const query = useQuery({ queryKey: api.keys.config, queryFn: api.config, retry: false });
  if (query.isPending) return <Skeleton lines={8} />;
  if (query.isError) return <ErrorState error={query.error} retry={() => void query.refetch()} />;
  return (
    <section aria-label="Model routing">
      <p className="muted ai-admin-lede">
        Each class has one primary model and as many ordered backups as you want. A call that fails on one row moves down to the next, so a provider outage
        degrades instead of stopping.
      </p>
      <StagedNotice routing={query.data.organizationRouting} />
      <RoutingEditor initial={query.data.routes} providers={query.data.providers} required={query.data.organizationRouting?.requiredClasses} />
    </section>
  );
}

/** USD per million tokens, shown to the cent a hundredth of a cent can matter. */
function priceLabel(usd: number): string {
  if (usd <= 0) return "—";
  return `$${usd >= 1 ? usd.toFixed(2) : usd.toFixed(3)}`;
}

const PROVIDER_ORDER = Object.keys(PROVIDER_NAMES) as AiProviderKind[];

/** Reduces a stored model to the body a save takes. */
function catalogWrite(model: AiCatalogModel): AiCatalogModelWrite {
  const { id: _id, updatedAt: _updatedAt, ...write } = model;
  return write;
}

/**
 * One line of a provider's list: what the provider reports, what is priced, or
 * both. `listed` is false for a priced model the provider no longer reports,
 * which is kept visible because a route may still point at it.
 */
type ModelRow = {
  model: string;
  priced?: AiCatalogModel;
  published?: AiModelPrices;
  listed: boolean;
};

/**
 * Which of each provider's models a platform-funded route may use, and what each
 * costs. The provider's own list is pulled through the connection that holds
 * its key, so nobody types an identifier; turning a model on is what makes it
 * routable. A model has to have a price before it can be turned on, because the
 * deployment bills customers the provider's price times a plan markup and one
 * with no price would be served at a loss. Prices the deployment already ships
 * are used as they are, so a known model is one switch.
 */
export function AiModelsPage() {
  const { client } = useAiAdmin();
  const catalog = useQuery({ queryKey: client.keys.catalog, queryFn: () => client.catalog!(), retry: false });
  const config = useQuery({ queryKey: client.keys.config, queryFn: client.config, retry: false });
  const [selected, setSelected] = useState<AiProviderKind | null>(null);
  if (catalog.isPending || config.isPending) return <Skeleton lines={8} />;
  if (catalog.isError) return <ErrorState error={catalog.error} retry={() => void catalog.refetch()} />;
  if (config.isError) return <ErrorState error={config.error} retry={() => void config.refetch()} />;
  const models = catalog.data.models;
  const providers = config.data.providers;
  // A provider earns a tab by having a connection or a priced model. One with
  // neither has nothing to list and nothing to turn on.
  const vendors = PROVIDER_ORDER.filter((vendor) => providers.some((p) => p.provider === vendor) || models.some((m) => m.provider === vendor));
  const vendor = selected && vendors.includes(selected) ? selected : vendors[0];
  return (
    <section aria-label="Models">
      <p className="muted ai-admin-lede">
        A route can only use a model that is turned on here. Prices are what the provider charges per million tokens; customers pay that times their plan's
        markup, so a model needs a price before it can be turned on.
      </p>
      {!vendor ? (
        <div className="card empty ai-provider-empty">
          <strong>No provider connections yet</strong>
          <span>Add one under Providers, then choose which of its models routes may use.</span>
        </div>
      ) : (
        <div className="card ai-models">
          <div className="ai-route-tabs" role="tablist" aria-label="Providers">
            {vendors.map((item) => {
              const on = models.filter((m) => m.provider === item && m.enabled !== false).length;
              return (
                <button key={item} type="button" role="tab" aria-selected={vendor === item} onClick={() => setSelected(item)} className="ai-route-tab">
                  {PROVIDER_NAMES[item]}
                  {on > 0 && <span className="ai-tab-count" aria-label={`${on} turned on`}>{on}</span>}
                </button>
              );
            })}
          </div>
          <ProviderModels
            key={vendor}
            vendor={vendor}
            priced={models.filter((m) => m.provider === vendor)}
            published={catalog.data.published ?? {}}
            connection={providers.find((p) => p.provider === vendor && p.configured && p.enabled) ?? providers.find((p) => p.provider === vendor && p.configured)}
          />
        </div>
      )}
    </section>
  );
}

function ProviderModels({
  vendor,
  priced,
  published,
  connection,
}: {
  vendor: AiProviderKind;
  priced: AiCatalogModel[];
  published: Record<string, AiModelPrices>;
  connection?: AiProviderConnection;
}) {
  const { client } = useAiAdmin();
  const qc = useQueryClient();
  const toast = useToast();
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(search.trim().toLowerCase()), 200);
    return () => window.clearTimeout(timer);
  }, [search]);
  const [pricing, setPricing] = useState<{ model?: string; existing?: AiCatalogModel } | null>(null);
  const [removing, setRemoving] = useState<AiCatalogModel | null>(null);
  // The provider's list, through the connection that holds its key. Azure
  // deployment names are chosen by whoever created them, so there is nothing
  // to pull; those are added by name.
  const listable = !!connection && vendor !== "azure";
  const discovery = useQuery({
    queryKey: client.keys.models(connection?.id ?? ""),
    queryFn: () => client.providerModels(connection!.id),
    enabled: listable,
    staleTime: 5 * 60 * 1000,
  });
  const rows = useMemo(() => {
    const byModel = new Map<string, ModelRow>();
    for (const m of priced) byModel.set(m.model, { model: m.model, priced: m, published: published[m.model], listed: false });
    for (const m of discovery.data?.models ?? connection?.models ?? []) {
      const row = byModel.get(m);
      if (row) row.listed = true;
      else byModel.set(m, { model: m, published: published[m], listed: true });
    }
    return [...byModel.values()].sort((a, b) => a.model.localeCompare(b.model));
  }, [priced, published, discovery.data, connection?.models]);
  // A provider can report well over a hundred models, most of them never going
  // to be routed to, so the list narrows by state as well as by name.
  const [show, setShow] = useState<"all" | "on" | "off">("all");
  const visible = rows.filter((r) => {
    if (show === "on" && !(r.priced && r.priced.enabled !== false)) return false;
    if (show === "off" && r.priced && r.priced.enabled !== false) return false;
    return !debounced || r.model.toLowerCase().includes(debounced) || !!r.priced?.displayName?.toLowerCase().includes(debounced);
  });

  const toggle = useMutation({
    mutationFn: ({ row, on }: { row: ModelRow; on: boolean }) =>
      row.priced
        ? client.saveCatalogModel!({ ...catalogWrite(row.priced), enabled: on }, row.priced.id)
        : client.saveCatalogModel!({ provider: vendor, model: row.model, ...row.published!, enabled: true }),
    onSuccess: async (saved) => {
      toast.push("success", saved.enabled ? `${saved.model} can now serve routes` : `${saved.model} is no longer offered to routes`);
      await qc.invalidateQueries({ queryKey: client.keys.catalog });
    },
    onError: (error) => toast.push("error", (error as Error).message),
  });
  // A model with no price cannot be turned on until it has one: the switch
  // opens the price form instead, and stays off until that is saved.
  const onToggle = (row: ModelRow, on: boolean) => {
    if (on && !row.priced && !row.published) {
      setPricing({ model: row.model });
      return;
    }
    toggle.mutate({ row, on });
  };
  const switching = (row: ModelRow) => toggle.isPending && toggle.variables?.row.model === row.model;
  const isOn = (row: ModelRow) => (switching(row) ? toggle.variables!.on : !!row.priced && row.priced.enabled !== false);

  const loading = listable && discovery.isPending;
  const columns: Column<ModelRow>[] = [
    {
      key: "model",
      header: "Model",
      label: "Model",
      render: (row) => (
        <>
          <code>{row.model}</code>
          {row.priced?.displayName && <span className="muted small ai-catalog-name">{row.priced.displayName}</span>}
          {row.priced && !row.listed && listable && !discovery.isPending && <span className="muted small ai-catalog-name">Not reported by the provider now</span>}
          {row.priced && row.priced.longContextThresholdTokens > 0 && (
            <span className="muted small ai-catalog-name">Long-context rates above {row.priced.longContextThresholdTokens.toLocaleString()} tokens</span>
          )}
        </>
      ),
    },
    { key: "input", header: "Input", label: "Input", className: "num", render: (row) => priceLabel((row.priced ?? row.published)?.inputUsd ?? 0) },
    { key: "output", header: "Output", label: "Output", className: "num", render: (row) => priceLabel((row.priced ?? row.published)?.outputUsd ?? 0) },
    { key: "cached", header: "Cached input", label: "Cached input", className: "num", render: (row) => priceLabel((row.priced ?? row.published)?.cachedInputUsd ?? 0) },
    {
      key: "batch",
      header: "Batch",
      label: "Batch",
      className: "num",
      render: (row) => {
        const discount = (row.priced ?? row.published)?.batchDiscount ?? 0;
        return discount > 0 ? `${Math.round(discount * 100)}%` : "—";
      },
    },
    {
      key: "on",
      header: "Available to routes",
      label: "Available to routes",
      className: "ai-model-switch",
      render: (row) => (
        <Switch
          checked={isOn(row)}
          onChange={(on) => onToggle(row, on)}
          disabled={switching(row)}
          label={<span className="sr-only">{`Available to routes: ${row.model}`}</span>}
          title={!row.priced && !row.published ? "Needs a price first" : undefined}
        />
      ),
    },
    {
      key: "actions",
      header: <span className="sr-only">Actions</span>,
      render: (row) => (
        <RowMenu label={`Actions for ${row.model}`}>
          {row.priced ? (
            <>
              <button type="button" role="menuitem" onClick={() => setPricing({ existing: row.priced })}>Edit prices</button>
              <button type="button" role="menuitem" className="danger" onClick={() => setRemoving(row.priced!)}>Remove model</button>
            </>
          ) : (
            <button type="button" role="menuitem" onClick={() => setPricing({ model: row.model })}>Set prices</button>
          )}
        </RowMenu>
      ),
    },
  ];

  return (
    <div className="ai-models-panel" role="tabpanel" aria-label={`${PROVIDER_NAMES[vendor]} models`}>
      <div className="ai-models-bar">
        <input
          className="input ai-models-search"
          type="search"
          aria-label="Search models"
          placeholder="Search models…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          autoComplete="off"
        />
        <select className="input ai-models-filter" aria-label="Show" autoComplete="off" value={show} onChange={(event) => setShow(event.target.value as typeof show)}>
          <option value="all">All models</option>
          <option value="on">Turned on</option>
          <option value="off">Turned off</option>
        </select>
        <span className="muted small ai-models-count">
          {!loading && rows.length > 0 && `${priced.filter((m) => m.enabled !== false).length} of ${rows.length} turned on`}
        </span>
        <button type="button" className="btn" onClick={() => setPricing({})}>Add model by name</button>
      </div>
      {!connection && (
        <p className="muted small ai-models-note">No configured {PROVIDER_NAMES[vendor]} connection, so the list is only what has been priced. Add one under Providers to pull the provider's models.</p>
      )}
      <div className="stack-table ai-catalog">
        <DataTable
          ariaLabel={`${PROVIDER_NAMES[vendor]} models`}
          columns={columns}
          rows={visible}
          rowKey={(row) => row.model}
          rowClassName={(row) => (isOn(row) ? "ai-model-row on" : "ai-model-row")}
          loading={loading}
          empty={
            rows.length === 0 ? (
              listable ? `${PROVIDER_NAMES[vendor]} reported no models for this connection.` : `No ${PROVIDER_NAMES[vendor]} models yet. Add one by name.`
            ) : (
              <>
                {show === "on" ? "No models are turned on" : show === "off" ? "Every model is turned on" : "No models match that search"}
                {debounced && " for that search"}.{" "}
                <button type="button" className="btn ghost sm" onClick={() => { setSearch(""); setShow("all"); }}>Clear filters</button>
              </>
            )
          }
        />
      </div>
      {pricing && (
        <CatalogModelDialog
          vendor={vendor}
          model={pricing.existing}
          identifier={pricing.model}
          suggested={pricing.model ? published[pricing.model] : undefined}
          existing={priced}
          onClose={() => setPricing(null)}
        />
      )}
      {removing && <RemoveCatalogModel model={removing} onClose={() => setRemoving(null)} />}
    </div>
  );
}

function RemoveCatalogModel({ model, onClose }: { model: AiCatalogModel; onClose: () => void }) {
  const { client } = useAiAdmin();
  const qc = useQueryClient();
  const toast = useToast();
  const remove = useMutation({
    mutationFn: () => client.deleteCatalogModel!(model.id),
    onSuccess: async () => {
      toast.push("success", `${model.model} is no longer available to routes`);
      await qc.invalidateQueries({ queryKey: client.keys.catalog });
      onClose();
    },
    onError: (error) => toast.push("error", (error as Error).message),
  });
  return (
    <ConfirmDialog
      open
      danger
      title="Remove model"
      confirmLabel={remove.isPending ? "Removing…" : "Remove model"}
      busy={remove.isPending}
      onConfirm={() => remove.mutate()}
      onClose={onClose}
    >
      <p>
        <code>{model.model}</code> will no longer be offered to routes. Nothing that already ran changes, and the calls it made keep the prices they were billed
        at.
      </p>
    </ConfirmDialog>
  );
}

/**
 * Prices one model, and says whether routes may use it. The provider is the tab
 * it was opened from. The identifier is fixed when the row came from the
 * provider's list, so it is stored exactly as reported and a call is priced by
 * an exact match on it; it is typed only for a model the provider does not
 * report, which for Azure is every deployment.
 */
function CatalogModelDialog({
  vendor,
  model,
  identifier: fixedIdentifier,
  suggested,
  existing,
  onClose,
}: {
  vendor: AiProviderKind;
  model?: AiCatalogModel;
  identifier?: string;
  suggested?: AiModelPrices;
  existing: AiCatalogModel[];
  onClose: () => void;
}) {
  const { client } = useAiAdmin();
  const qc = useQueryClient();
  const toast = useToast();
  const formId = useId();
  const provider = model?.provider ?? vendor;
  const fixed = !!model || !!fixedIdentifier;
  const start: Partial<AiModelPrices> = model ?? suggested ?? {};
  const price = (usd?: number) => (usd ? String(usd) : "");
  const [identifier, setIdentifier] = useState(model?.model ?? fixedIdentifier ?? "");
  const [displayName, setDisplayName] = useState(model?.displayName ?? "");
  const [input, setInput] = useState(model ? String(model.inputUsd) : price(start.inputUsd));
  const [output, setOutput] = useState(model ? String(model.outputUsd) : price(start.outputUsd));
  const [cached, setCached] = useState(price(start.cachedInputUsd));
  const [cacheWrite, setCacheWrite] = useState(price(start.cacheWriteUsd));
  const [longThreshold, setLongThreshold] = useState(start.longContextThresholdTokens ? String(start.longContextThresholdTokens) : "");
  const [longInput, setLongInput] = useState(price(start.longInputUsd));
  const [longOutput, setLongOutput] = useState(price(start.longOutputUsd));
  const [longCached, setLongCached] = useState(price(start.longCachedInputUsd));
  const [longCacheWrite, setLongCacheWrite] = useState(price(start.longCacheWriteUsd));
  const [batch, setBatch] = useState(price(start.batchDiscount));
  const [note, setNote] = useState(start.note ?? "");
  const [enabled, setEnabled] = useState(model ? model.enabled !== false : true);
  // What is saved, for an existing model; nothing, for a new one, so a form
  // arriving pre-filled with a shipped price still counts as something to save.
  const baseline = model
    ? [model.model, model.displayName ?? "", String(model.inputUsd), String(model.outputUsd), price(model.cachedInputUsd), price(model.cacheWriteUsd), model.longContextThresholdTokens ? String(model.longContextThresholdTokens) : "", price(model.longInputUsd), price(model.longOutputUsd), price(model.longCachedInputUsd), price(model.longCacheWriteUsd), price(model.batchDiscount), model.note ?? "", model.enabled !== false]
    : null;
  const values = [identifier, displayName, input, output, cached, cacheWrite, longThreshold, longInput, longOutput, longCached, longCacheWrite, batch, note, enabled];
  const dirty = !baseline || !settingsEqual(values, baseline);
  const requestClose = () => {
    if (dirty && !window.confirm("Discard unsaved model changes?")) return;
    onClose();
  };

  const duplicate = !model && existing.some((m) => m.model === identifier.trim());
  const inputUsd = Number(input);
  const outputUsd = Number(output);
  const priced = (input.trim() !== "" && inputUsd > 0) || (output.trim() !== "" && outputUsd > 0);
  const longValues = [longInput, longOutput, longCached, longCacheWrite];
  const hasLongPrices = longValues.some((v) => Number(v) > 0);
  const hasLongThreshold = Number(longThreshold) > 0;
  const valid =
    identifier.trim() !== "" &&
    !duplicate &&
    priced &&
    hasLongPrices === hasLongThreshold &&
    [input, output, cached, cacheWrite, longThreshold, ...longValues, batch].every((v) => v.trim() === "" || Number.isFinite(Number(v)));

  const save = useMutation({
    mutationFn: () =>
      client.saveCatalogModel!(
        {
          provider,
          model: identifier.trim(),
          displayName: displayName.trim() || undefined,
          inputUsd: Number(input) || 0,
          outputUsd: Number(output) || 0,
          cachedInputUsd: Number(cached) || 0,
          cacheWriteUsd: Number(cacheWrite) || 0,
          longContextThresholdTokens: Number(longThreshold) || 0,
          longInputUsd: Number(longInput) || 0,
          longOutputUsd: Number(longOutput) || 0,
          longCachedInputUsd: Number(longCached) || 0,
          longCacheWriteUsd: Number(longCacheWrite) || 0,
          batchDiscount: Number(batch) || 0,
          note: note.trim() || undefined,
          enabled,
        },
        model?.id,
      ),
    onSuccess: async (saved) => {
      toast.push("success", saved.enabled ? `${saved.model} can now serve routes` : `${saved.model} prices saved`);
      await qc.invalidateQueries({ queryKey: client.keys.catalog });
      onClose();
    },
    onError: (error) => toast.push("error", (error as Error).message),
  });

  return (
    <Dialog
      open
      onClose={requestClose}
      title={model ? "Edit model prices" : fixed ? "Price model" : "Add model"}
      width={560}
      className="ai-catalog-dialog"
      footer={
        <>
          <button type="button" className="btn ghost" onClick={requestClose} disabled={save.isPending}>Cancel</button>
          <button type="submit" form={formId} className="btn primary" disabled={!valid || !dirty || save.isPending}>
            {save.isPending ? "Saving…" : model ? "Save prices" : enabled ? "Save and turn on" : "Save prices"}
          </button>
        </>
      }
    >
      <form
        id={formId}
        className="ai-catalog-form"
        autoComplete="off"
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          if (valid) save.mutate();
        }}
      >
        <UnsavedGuard dirty={dirty} />
        <Field label="Model" hint={fixed ? `${PROVIDER_NAMES[provider]}. Stored exactly as the provider reports it.` : `${PROVIDER_NAMES[provider]}. Exactly as the provider names it: a call is priced by an exact match.`} error={duplicate ? "This model is already listed." : undefined}>
          {(p) =>
            fixed ? (
              <input {...p} className="input" autoComplete="off" value={identifier} readOnly />
            ) : (
              <input
                {...p}
                className="input"
                autoComplete="off"
                value={identifier}
                onChange={(event) => setIdentifier(event.target.value)}
                placeholder={provider === "azure" ? "Deployment name" : "Model identifier"}
                required
              />
            )
          }
        </Field>
        {suggested && !model && <p className="muted small ai-catalog-suggested">Filled in from the prices this deployment ships. Check them against the provider's current rates.</p>}
        <Field label="Display name (optional)" hint="Shown alongside the identifier in this list.">
          {(p) => <input {...p} className="input" autoComplete="off" value={displayName} onChange={(event) => setDisplayName(event.target.value)} />}
        </Field>
        <div className="field-row">
          <Field label="Input (USD per million)">
            {(p) => <input {...p} className="input" type="number" min="0" step="0.001" autoComplete="off" value={input} onChange={(event) => setInput(event.target.value)} />}
          </Field>
          <Field label="Output (USD per million)">
            {(p) => <input {...p} className="input" type="number" min="0" step="0.001" autoComplete="off" value={output} onChange={(event) => setOutput(event.target.value)} />}
          </Field>
        </div>
        <div className="field-row">
          <Field label="Cached input (optional)" hint="What a cached input token costs instead of the input price.">
            {(p) => <input {...p} className="input" type="number" min="0" step="0.001" autoComplete="off" value={cached} onChange={(event) => setCached(event.target.value)} />}
          </Field>
          <Field label="Cache write (optional)" hint="What a token newly written to prompt cache costs.">
            {(p) => <input {...p} className="input" type="number" min="0" step="0.001" autoComplete="off" value={cacheWrite} onChange={(event) => setCacheWrite(event.target.value)} />}
          </Field>
        </div>
        <details className="ai-catalog-pricing-details" open={hasLongThreshold || hasLongPrices}>
          <summary>Long-context pricing</summary>
          <p className="muted small">Set this only when the provider changes the full request price above an input-token threshold.</p>
          <Field label="Threshold (input tokens)">
            {(p) => <input {...p} className="input" type="number" min="0" step="1" autoComplete="off" value={longThreshold} onChange={(event) => setLongThreshold(event.target.value)} />}
          </Field>
          <div className="field-row">
            <Field label="Long input">
              {(p) => <input {...p} className="input" type="number" min="0" step="0.001" autoComplete="off" value={longInput} onChange={(event) => setLongInput(event.target.value)} />}
            </Field>
            <Field label="Long output">
              {(p) => <input {...p} className="input" type="number" min="0" step="0.001" autoComplete="off" value={longOutput} onChange={(event) => setLongOutput(event.target.value)} />}
            </Field>
          </div>
          <div className="field-row">
            <Field label="Long cached input">
              {(p) => <input {...p} className="input" type="number" min="0" step="0.001" autoComplete="off" value={longCached} onChange={(event) => setLongCached(event.target.value)} />}
            </Field>
            <Field label="Long cache write">
              {(p) => <input {...p} className="input" type="number" min="0" step="0.001" autoComplete="off" value={longCacheWrite} onChange={(event) => setLongCacheWrite(event.target.value)} />}
            </Field>
          </div>
        </details>
        <div className="field-row">
          <Field label="Batch discount (optional)" hint="A multiplier, so 0.5 is half price.">
            {(p) => <input {...p} className="input" type="number" min="0" max="1" step="0.05" autoComplete="off" value={batch} onChange={(event) => setBatch(event.target.value)} />}
          </Field>
          <span />
        </div>
        <Field label="Note (optional)">
          {(p) => <input {...p} className="input" autoComplete="off" value={note} onChange={(event) => setNote(event.target.value)} placeholder="e.g. Bedrock on-demand" />}
        </Field>
        <Switch checked={enabled} onChange={setEnabled} label="Available to routes" hint="Off keeps the prices on file without offering the model to routes." />
        {!priced && <p className="muted small">An input or output price is required: a model priced at nothing would be served for free.</p>}
        {hasLongPrices !== hasLongThreshold && <p className="muted small">Long-context prices and their token threshold must be set together.</p>}
      </form>
    </Dialog>
  );
}

/**
 * The frame these pages share. The deployment module supplies the paths, since
 * SaaS mounts them under /staff and a self-hosted install under /admin.
 */
export function AiAdminLayout({
  providersTo,
  modelsTo,
  routingTo,
  children,
}: {
  providersTo: string;
  modelsTo: string;
  routingTo: string;
  children: ReactNode;
}) {
  return (
    <div className="page ai-admin">
      <PageHeader title="AI setup" />
      <SectionTabs label="AI setup">
        <Link to={providersTo as never} activeOptions={{ exact: true }}>Providers</Link>
        <Link to={modelsTo as never}>Models</Link>
        <Link to={routingTo as never}>Routing</Link>
      </SectionTabs>
      {children}
    </div>
  );
}
